import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import AgGridTable from "../../../components/table/AgGridTable";
import {
  Button,
  Card,
  Chip,
  Tooltip,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AccordionSummaryProps,
  Popper,
  TextField,
  useTheme,
  InputAdornment,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import SearchIcon from "@mui/icons-material/Search";

import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { getStatus } from "../../../axios/constants";
import { formatScanType } from "../scansV2/scansV2.utils";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { setEdit } from "./MyScanTable_Details_slice";
import Divider from "@mui/material/Divider";
import * as React from "react";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

import MapCredentialPopup from "../assetsManagement/MapCredentialPopup";
import { toast } from "react-toastify";
import WhiteTooltip from "../../../components/WhiteTooltip";

// 🚀 Global variables to store filter data for AG Grid filters
let globalScanStatuses: string[] = [];
let globalScanVendors: string[] = [];
let globalScanTypes: string[] = [];
let globalScanRegions: string[] = [];
let globalScanAssetNames: string[] = [];

type ScanFilterKey = "assetName" | "vendor" | "type" | "region" | "status";

// 🚀 Custom Status Filter Component for AG Grid
const ScanStatusFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>(globalScanStatuses);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  // Filter statuses based on search text
  const filteredStatuses = React.useMemo(() => {
    if (!searchText.trim()) return availableStatuses;
    return availableStatuses.filter(status =>
      status.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableStatuses, searchText]);

  // Update available statuses when global variable changes
  React.useEffect(() => {
    const checkForUpdates = () => {
      if (globalScanStatuses.length > 0 && availableStatuses.length === 0) {
        setAvailableStatuses([...globalScanStatuses]);
      }
    };

    const interval = setInterval(checkForUpdates, 500);
    checkForUpdates();

    return () => clearInterval(interval);
  }, [availableStatuses.length]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedStatuses.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedStatuses.length === 0 || isAllSelected) return true;
      return selectedStatuses.includes(params.data.status);
    },

    getModel: () => {
      return selectedStatuses.length > 0 && !isAllSelected ? { selectedTypes: selectedStatuses } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedStatuses(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedStatuses([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableStatuses([...globalScanStatuses]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleStatusToggle = (status: string) => {
    if (status === 'All') {
      setIsAllSelected(true);
      setSelectedStatuses([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedStatuses.includes(status)
        ? selectedStatuses.filter(type => type !== status)
        : [...selectedStatuses, status];

      setSelectedStatuses(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedStatuses([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scanStatusFilter = [];
    window.dispatchEvent(new CustomEvent('scanStatusFilterChanged', {
      detail: { selectedTypes: [] }
    }));
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Status
      </div>

      {/* Search Input */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        {/* All option */}
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleStatusToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleStatusToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredStatuses.length > 0 ? (
          filteredStatuses.map((status: string) => {
            const isSelected = selectedStatuses.includes(status);
            return (
              <div
                key={status}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleStatusToggle(status)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleStatusToggle(status)}
                  style={{
                    marginRight: '8px'
                  }}
                />
                <span>{status}</span>
              </div>
            );
          })
        ) : availableStatuses.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading statuses...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No statuses found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).scanStatusFilter = isAllSelected ? [] : selectedStatuses;
            window.dispatchEvent(new CustomEvent('scanStatusFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedStatuses }
            }));
            setTimeout(() => {
              if (params.api) {
                params.api.onFilterChanged();
              }
            }, 0);
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom Vendor Filter Component for AG Grid
const ScanVendorFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [availableVendors, setAvailableVendors] = useState<string[]>(globalScanVendors);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const filteredVendors = React.useMemo(() => {
    if (!searchText.trim()) return availableVendors;
    return availableVendors.filter(vendor =>
      vendor.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableVendors, searchText]);

  React.useEffect(() => {
    const checkForUpdates = () => {
      if (globalScanVendors.length > 0 && availableVendors.length === 0) {
        setAvailableVendors([...globalScanVendors]);
      }
    };

    const interval = setInterval(checkForUpdates, 500);
    checkForUpdates();

    return () => clearInterval(interval);
  }, [availableVendors.length]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedVendors.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedVendors.length === 0 || isAllSelected) return true;
      return selectedVendors.includes(params.data.vendor);
    },

    getModel: () => {
      return selectedVendors.length > 0 && !isAllSelected ? { selectedTypes: selectedVendors } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedVendors(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedVendors([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableVendors([...globalScanVendors]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleVendorToggle = (vendor: string) => {
    if (vendor === 'All') {
      setIsAllSelected(true);
      setSelectedVendors([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedVendors.includes(vendor)
        ? selectedVendors.filter(type => type !== vendor)
        : [...selectedVendors, vendor];

      setSelectedVendors(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedVendors([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scanVendorFilter = [];
    window.dispatchEvent(new CustomEvent('scanVendorFilterChanged', {
      detail: { selectedTypes: [] }
    }));
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Vendor
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleVendorToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleVendorToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredVendors.length > 0 ? (
          filteredVendors.map((vendor: string) => {
            const isSelected = selectedVendors.includes(vendor);
            return (
              <div
                key={vendor}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleVendorToggle(vendor)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleVendorToggle(vendor)}
                  style={{ marginRight: '8px' }}
                />
                <span>{vendor}</span>
              </div>
            );
          })
        ) : availableVendors.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading vendors...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No vendors found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).scanVendorFilter = isAllSelected ? [] : selectedVendors;
            window.dispatchEvent(new CustomEvent('scanVendorFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedVendors }
            }));
            setTimeout(() => {
              if (params.api) {
                params.api.onFilterChanged();
              }
            }, 0);
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom Type Filter Component for AG Grid
const ScanTypeFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>(globalScanTypes);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const filteredTypes = React.useMemo(() => {
    if (!searchText.trim()) return availableTypes;
    return availableTypes.filter(type =>
      type.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableTypes, searchText]);

  React.useEffect(() => {
    const checkForUpdates = () => {
      if (globalScanTypes.length > 0 && availableTypes.length === 0) {
        setAvailableTypes([...globalScanTypes]);
      }
    };

    const interval = setInterval(checkForUpdates, 500);
    checkForUpdates();

    return () => clearInterval(interval);
  }, [availableTypes.length]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedTypes.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedTypes.length === 0 || isAllSelected) return true;
      return selectedTypes.includes(params.data.type);
    },

    getModel: () => {
      return selectedTypes.length > 0 && !isAllSelected ? { selectedTypes: selectedTypes } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedTypes(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedTypes([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableTypes([...globalScanTypes]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleTypeToggle = (type: string) => {
    if (type === 'All') {
      setIsAllSelected(true);
      setSelectedTypes([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedTypes.includes(type)
        ? selectedTypes.filter(t => t !== type)
        : [...selectedTypes, type];

      setSelectedTypes(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedTypes([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scanTypeFilter = [];
    window.dispatchEvent(new CustomEvent('scanTypeFilterChanged', {
      detail: { selectedTypes: [] }
    }));
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Type
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleTypeToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleTypeToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredTypes.length > 0 ? (
          filteredTypes.map((type: string) => {
            const isSelected = selectedTypes.includes(type);
            return (
              <div
                key={type}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleTypeToggle(type)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleTypeToggle(type)}
                  style={{ marginRight: '8px' }}
                />
                <span>{type}</span>
              </div>
            );
          })
        ) : availableTypes.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading types...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No types found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).scanTypeFilter = isAllSelected ? [] : selectedTypes;
            window.dispatchEvent(new CustomEvent('scanTypeFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedTypes }
            }));
            setTimeout(() => {
              if (params.api) {
                params.api.onFilterChanged();
              }
            }, 0);
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom Region Filter Component for AG Grid
const ScanRegionFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [availableRegions, setAvailableRegions] = useState<string[]>(globalScanRegions);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const filteredRegions = React.useMemo(() => {
    if (!searchText.trim()) return availableRegions;
    return availableRegions.filter(region =>
      region.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableRegions, searchText]);

  React.useEffect(() => {
    const checkForUpdates = () => {
      if (globalScanRegions.length > 0 && availableRegions.length === 0) {
        setAvailableRegions([...globalScanRegions]);
      }
    };

    const interval = setInterval(checkForUpdates, 500);
    checkForUpdates();

    return () => clearInterval(interval);
  }, [availableRegions.length]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedRegions.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedRegions.length === 0 || isAllSelected) return true;
      return selectedRegions.includes(params.data.region);
    },

    getModel: () => {
      return selectedRegions.length > 0 && !isAllSelected ? { selectedTypes: selectedRegions } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedRegions(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedRegions([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableRegions([...globalScanRegions]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleRegionToggle = (region: string) => {
    if (region === 'All') {
      setIsAllSelected(true);
      setSelectedRegions([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedRegions.includes(region)
        ? selectedRegions.filter(r => r !== region)
        : [...selectedRegions, region];

      setSelectedRegions(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedRegions([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scanRegionFilter = [];
    window.dispatchEvent(new CustomEvent('scanRegionFilterChanged', {
      detail: { selectedTypes: [] }
    }));
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Region
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleRegionToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleRegionToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredRegions.length > 0 ? (
          filteredRegions.map((region: string) => {
            const isSelected = selectedRegions.includes(region);
            return (
              <div
                key={region}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleRegionToggle(region)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleRegionToggle(region)}
                  style={{ marginRight: '8px' }}
                />
                <span>{region}</span>
              </div>
            );
          })
        ) : availableRegions.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading regions...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No regions found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).scanRegionFilter = isAllSelected ? [] : selectedRegions;
            window.dispatchEvent(new CustomEvent('scanRegionFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedRegions }
            }));
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom Asset Name Filter Component for AG Grid
const ScanAssetNameFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedAssetNames, setSelectedAssetNames] = useState<string[]>([]);
  const [availableAssetNames, setAvailableAssetNames] = useState<string[]>(globalScanAssetNames);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const filteredAssetNames = React.useMemo(() => {
    if (!searchText.trim()) return availableAssetNames;
    return availableAssetNames.filter(name =>
      name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableAssetNames, searchText]);

  React.useEffect(() => {
    const checkForUpdates = () => {
      if (globalScanAssetNames.length > 0 && availableAssetNames.length === 0) {
        setAvailableAssetNames([...globalScanAssetNames]);
      }
    };

    const interval = setInterval(checkForUpdates, 500);
    checkForUpdates();

    return () => clearInterval(interval);
  }, [availableAssetNames.length]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedAssetNames.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedAssetNames.length === 0 || isAllSelected) return true;
      return selectedAssetNames.includes(params.data.assetName);
    },

    getModel: () => {
      return selectedAssetNames.length > 0 && !isAllSelected ? { selectedTypes: selectedAssetNames } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedAssetNames(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedAssetNames([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableAssetNames([...globalScanAssetNames]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleAssetNameToggle = (name: string) => {
    if (name === 'All') {
      setIsAllSelected(true);
      setSelectedAssetNames([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedAssetNames.includes(name)
        ? selectedAssetNames.filter(n => n !== name)
        : [...selectedAssetNames, name];

      setSelectedAssetNames(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedAssetNames([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scanAssetNameFilter = [];
    window.dispatchEvent(new CustomEvent('scanAssetNameFilterChanged', {
      detail: { selectedTypes: [] }
    }));
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Asset Name
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleAssetNameToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleAssetNameToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredAssetNames.length > 0 ? (
          filteredAssetNames.map((name: string) => {
            const isSelected = selectedAssetNames.includes(name);
            return (
              <div
                key={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleAssetNameToggle(name)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleAssetNameToggle(name)}
                  style={{ marginRight: '8px' }}
                />
                <span>{name}</span>
              </div>
            );
          })
        ) : availableAssetNames.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading asset names...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No asset names found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).scanAssetNameFilter = isAllSelected ? [] : selectedAssetNames;
            window.dispatchEvent(new CustomEvent('scanAssetNameFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedAssetNames }
            }));
            setTimeout(() => {
              if (params.api) {
                params.api.onFilterChanged();
              }
            }, 0);
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

const ScanInformation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { scan_uuid } = useParams<{ scan_uuid: string }>();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [scheduleRowData, setScheduleRowData] = useState<any>([]);
  const gridApiRef = useRef<any>(null);

  const scanId = useSelector((state: RootState) => state.onPremMyScan.scan_id);
  const statusTemp = useSelector(
    (state: RootState) => state.onPremMyScan.statusTemp
  );
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    assetName: [],
    vendor: [],
    type: [],
    region: [],
    status: [],
  });

  useEffect(() => {
    const updateFilter = (key: ScanFilterKey, selectedValues: string[]) => {
      setActiveFilters((prev) => ({
        ...prev,
        [key]: selectedValues,
      }));
    };

    const handleStatusFilterChange = (event: any) => {
      updateFilter("status", event?.detail?.selectedTypes ?? []);
    };

    const handleVendorFilterChange = (event: any) => {
      updateFilter("vendor", event?.detail?.selectedTypes ?? []);
    };

    const handleTypeFilterChange = (event: any) => {
      updateFilter("type", event?.detail?.selectedTypes ?? []);
    };

    const handleRegionFilterChange = (event: any) => {
      updateFilter("region", event?.detail?.selectedTypes ?? []);
    };

    const handleAssetNameFilterChange = (event: any) => {
      updateFilter("assetName", event?.detail?.selectedTypes ?? []);
    };

    window.addEventListener("scanStatusFilterChanged", handleStatusFilterChange);
    window.addEventListener("scanVendorFilterChanged", handleVendorFilterChange);
    window.addEventListener("scanTypeFilterChanged", handleTypeFilterChange);
    window.addEventListener("scanRegionFilterChanged", handleRegionFilterChange);
    window.addEventListener("scanAssetNameFilterChanged", handleAssetNameFilterChange);

    return () => {
      window.removeEventListener("scanStatusFilterChanged", handleStatusFilterChange);
      window.removeEventListener("scanVendorFilterChanged", handleVendorFilterChange);
      window.removeEventListener("scanTypeFilterChanged", handleTypeFilterChange);
      window.removeEventListener("scanRegionFilterChanged", handleRegionFilterChange);
      window.removeEventListener("scanAssetNameFilterChanged", handleAssetNameFilterChange);
    };
  }, []);
  const [regionAnchorEl, setRegionAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [currentRegions, setCurrentRegions] = useState<any>([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [originalRegions, setOriginalRegions] = useState([]);
  //  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const myScanStatusList = useSelector(
    (state: RootState) => state.MyScanDetails.myscanStatusList
  );
  const myscanSocketData = useSelector(
    (state: RootState) => state.MyScanDetails.myscanSocketData
  );
  const scanTime = useSelector(
    (state: RootState) => state.MyScanDetails.scanTime
  );
  const onprem_posture_id = useSelector(
    (state: RootState) => state.MyScanDetails.onprem_posture_id
  );
  console.log("scanTime", scanTime);
  console.log("myScanStatusList", myScanStatusList);

  // const [device, setDevice] = useState("");

  const [vendors, setVendors] = useState<any[]>([]);
  const [expandedVendor, setExpandedVendor] = useState<string | false>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [loadingVendors, setLoadingVendors] = useState(false);

  const [vendorAnchorEl, setVendorAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const vendorDropdownOpen = Boolean(vendorAnchorEl);
  const [isMapCredentialPopupOpen, setIsMapCredentialPopupOpen] =
    useState(false);

  const [credTypes, setCredtypes] = useState([]);
  const [assetsCredData, setAssetsCredData] = useState([]);

  const [isCancelSchedulePopupOpen, setIsCancelSchedulePopupOpen] =
    useState(false);
  const [removeAllSchedules, setRemoveAllSchedules] = useState(false);
  const handleCancelScheduleClose = () => {
    setIsCancelSchedulePopupOpen(false);
    setRemoveAllSchedules(false);
    setExpandedVendor(false);
    setSelectedCategory("");
  };
  const handleRemoveSchedule = async () => {
    // Implement remove schedule logic here
    setIsCancelSchedulePopupOpen(false);
    const selectedVendor = vendors.find((v) => v.id === expandedVendor);
    if (removeAllSchedules) {
      handleMap();
    } else {
      const scan_data = selectedRows.map((row) => ({
        scan_id: scan_uuid,
        new_cred: false,

        new_scan_det_id: row.file_id,
        vendor: selectedVendor ? selectedVendor.id : undefined,
        category_id: selectedCategory,
      }));
      const payload = {
        failed_rescan: true,
        scan_source: "Scans",
        scan_data,
      };
      try {
        await axiosInstance.post("/securityscan/rescan", payload);
        // Optionally, show a success toast or refresh data
        fetchScheduleData();
        toast.success("Rescan triggered successfully");

      } catch (error: any) {
        toast.error(
          error.response?.data?.message || "Failed to trigger rescan",
          { type: "error" }
        );
      } finally {
        // refresh the page here
        setRegionAnchorEl(null);
        setSelectedRows([]);
        setVendorAnchorEl(null);
        setSelectedRows([]);
        if (gridApiRef.current && gridApiRef.current.api) {
          gridApiRef.current.api.deselectAll();
        }

        setExpandedVendor(false);
        setSelectedCategory("");
        setIsMapCredentialPopupOpen(false);
      }
    }
  };

  const handleMapPopuplose = () => {
    setIsMapCredentialPopupOpen(false);
  };

  const mapCallback = async (response: any) => {
    console.log("Re-scan with credential triggered", response);
    // Prepare scan_data array
    const selectedVendor = vendors.find((v) => v.id === expandedVendor);
    // console.log("selectedVendor", selectedVendor);
    console.log("removeAllSchedules", removeAllSchedules);
    console.log(response, "response response");

    const credId = response.selectedCredenntialId
      ? response.selectedCredenntialId
      : response.data?.id;
    console.log("credId", credId);
    const scan_data = selectedRows.map((row) => ({
      scan_id: scan_uuid,
      new_cred: false,
      cred_id: credId,
      new_scan_det_id: row.file_id,
      vendor: selectedVendor ? selectedVendor.id : undefined,
      category_id: selectedCategory,
    }));
    const payload = {
      failed_rescan: true,
      scan_source: "Scans",
      scan_data,
    };
    try {
      await axiosInstance.post("/securityscan/rescan", payload);
      // Optionally, show a success toast or refresh data
      fetchScheduleData();
      toast.success("Rescan triggered successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to trigger rescan", {
        type: "error",
      });
    }
    // refresh the page here
    setIsMapCredentialPopupOpen(false);
    setExpandedVendor(false);
    setSelectedCategory("");
    setVendorAnchorEl(null);
    setSelectedRows([]);
    if (gridApiRef.current && gridApiRef.current.api) {
      gridApiRef.current.api.deselectAll();
    }
  };

  const handleMap = async () => {
    setIsMapCredentialPopupOpen(true);
    try {
      const response = await axiosInstance.get(END_POINTS.CREDENTIAL_MGMT);
      if (response?.status === 200) {
        setAssetsCredData(response?.data?.data?.cred_list);
      }
    } catch (error: any) {
      toast.error(
        error.response.data
          ? error.response.data.message
          : "Something went wrong",
        { type: "error" }
      );
    }
    try {
      const response = await axiosInstance.get(END_POINTS.CREDENTIAL_TYPES);
      if (response?.status === 200) {
        setCredtypes(response?.data?.data);
      }
    } catch (error: any) {
      toast.error(
        error.response.data
          ? error.response.data.message
          : "Something went wrong",
        { type: "error" }
      );
    }
  };

  const handleVendorButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setVendorAnchorEl(event.currentTarget);
  };
  const handleVendorDropdownClose = () => {
    console.log("Selected Rows on Vendor Dropdown Close:", selectedRows);
    setVendorAnchorEl(null);
    setExpandedVendor(false);
    setSelectedCategory("");
  };
  const handlePoPoverRegionClose = () => {
    console.log("Selected Rows on Region Dropdown Close:", selectedRows);
    setRegionAnchorEl(null);
    setExpanded({});
    setSelectedRegionId(null);
  }
  const handleApply = () => {
    const selectedVendor = vendors.find((v) => v.id === expandedVendor);
    console.log("Selected Rows on Vendor Dropdown Close:", selectedRows);
    console.log("Selected Vendor:", selectedVendor);
    console.log("Selected Category:", selectedCategory);

    setIsCancelSchedulePopupOpen(true);

  }

  console.log(scheduleRowData, "scheduleRowDatascheduleRowData");

  const handleSelectionChange = (selectedRows: any[]) => {
    console.log("Selected rows:", selectedRows);
    setSelectedRows(selectedRows);
    // Handle selection change logic here
  };

  const handleSave = async () => {
    if (selectedRegionId && selectedRows?.length > 0) {
      const payload = {
        region_id: selectedRegionId,
        device_id: selectedRows.map((device) => device.device_id),
      };
      try {
        const response = await axiosInstance.post(
          END_POINTS.UPDATE_DEVICE,
          payload
        );
        if (response.status === 200) {
          toast.success(response.data.message);
          setRegionAnchorEl(null);
          setExpanded({});
          setSelectedRegionId(null);
          // setOpenSideNav(false);
          // fetchAssetsData();
          setSelectedRows([]);
          fetchScheduleData();

        }
      } catch (error: any) {
        toast.error(
          error.response.data
            ? error.response.data.message
            : "Something went wrong",
          { type: "error" }
        );
      }
    }
  };
  const setRegionData = async () => {
    const response = await axiosInstance.get(
      END_POINTS.GLOBAL_DASHBOARD_REGION
    );
    if (response?.status === 200) {
      setCurrentRegions(response?.data?.data?.regions_list);
      setOriginalRegions(response?.data?.data?.regions_list);
    }
  };

  const handleRegionClose = () => {
    handleSave();
    setRegionAnchorEl(null);
  }

  const handleRadioChange = (id: any) => {
    setSelectedRegionId(id);
  };

  const RegionTree = ({ nodes, selectedId, }: any) => {
    const toggleExpand = (id: any) => {
      setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    };



    const renderTree = (node: any, isLastChild: any) => (
      <li key={node.id} style={{ marginBottom: 8 }}>
        {/* Expand/Collapse Icon */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {" "}
          {node.data?.length > 0 && (
            <span
              onClick={() => toggleExpand(node.id)}
              style={{
                cursor: "pointer",
                marginRight: 8,
                display: "inline-block",
                width: 16,
                textAlign: "center",
              }}
            >
              {expanded[node.id] ? "▼" : "▶"}
            </span>
          )}
          {/* Radio Button and Label */}
          <label
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "1.2rem",
              fontWeight: "500",
            }}
          >
            {isLastChild && (
              <input
                type="radio"
                name="region"
                checked={selectedId === node.id}
                onChange={() => handleRadioChange(node.id)}
                style={{ marginRight: 8, transform: "scale(1.5)" }}
              />
            )}
            {node.label}
          </label>
        </div>

        {/* Render Children */}
        {expanded[node.id] && node.data?.length > 0 && (
          <ul style={{ listStyle: "none", paddingLeft: 20 }}>
            {node.data.map((childNode: any) => {
              const isLastChild =
                !childNode.data || childNode.data.length === 0;
              return (
                <li
                  key={childNode.id}
                  style={{ marginBottom: isLastChild ? 0 : 8 }}
                >
                  {renderTree(childNode, isLastChild)}
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );

    return (
      <ul style={{ listStyle: "none", paddingLeft: 0 }}>
        {nodes.map((node: any) => {
          const isLastChild = !node.data || node.data.length === 0;
          return (
            <li key={node.id} style={{ marginBottom: isLastChild ? 0 : 8 }}>
              {renderTree(node, isLastChild)}
            </li>
          );
        })}
      </ul>
    );
  };

  const regionDropdown = (
    <Popper open={Boolean(regionAnchorEl)} anchorEl={regionAnchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
      <Card sx={{ minWidth: 350, p: 2, boxShadow: 3 }}>
        {/* <input
          type="text"
          placeholder="Search.."
          value={regionSearch}
          onChange={e => setRegionSearch(e.target.value)}
          style={{ width: "100%", marginBottom: 8, padding: 6, borderRadius: 4, border: '1px solid #eee' }}
        /> */}
        <TextField
          variant="outlined"
          placeholder="Search..."
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          onChange={(e) => {
            const searchValue = e.target.value.toLowerCase();
            if (searchValue) {
              const filterRegions = (regions: any[]): any[] => {
                return regions
                  .map((region) => {
                    if (region.label.toLowerCase().includes(searchValue)) {
                      setExpanded((prev) => ({ ...prev, [region.id]: true }));
                      return region;
                    }
                    if (region.data?.length) {
                      const filteredChildren = filterRegions(region.data);
                      if (filteredChildren.length) {
                        setExpanded((prev) => ({ ...prev, [region.id]: true }));
                        return { ...region, data: filteredChildren };
                      }
                    }
                    return null;
                  })
                  .filter(Boolean);
              };
              setCurrentRegions(filterRegions(originalRegions));
            } else {
              setCurrentRegions(originalRegions);
              setExpanded({});
            }
          }}
        />
        <div style={{ maxHeight: 250, overflowY: 'auto' }}>
          {/* {regionOptions.filter(region => region.name.toLowerCase().includes(regionSearch.toLowerCase()) || region.countries.some(c => c.name.toLowerCase().includes(regionSearch.toLowerCase()))).map(region => (
            <div key={region.name} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, cursor: 'pointer', marginBottom: 4 }}>{region.name}</div>
              {region.countries.map(country => (
                <div key={country.name} style={{ marginLeft: 16, marginBottom: 4 }}>
                  <div style={{ fontWeight: 500 }}>{country.name}</div>
                  {country.states.map(state => (
                    <div key={state} style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="radio" name="regionState" />
                      <span>{state}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))} */}
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <RegionTree
              nodes={currentRegions}
              selectedId={selectedRegionId}
              setSelectedId={setSelectedRegionId}
            />
            <div style={{ borderTop: '1px solid #e0e0e0', margin: '16px 0 0 0', width: '100%' }} />
          </Box>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button
            variant="contained"
            sx={{
              minWidth: 100,
              borderRadius: '8px',
              background: '#4B2FFB',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': { background: '#3a23c8' }
            }}
            onClick={handleRegionClose}
          >
            Apply
          </Button>
        </div>
        {/* <Button variant="contained" sx={{ mt: 2, width: '100%' }} onClick={handleRegionClose}>Apply</Button> */}
      </Card>
    </Popper>
  );

  const CustomTooltipCell = (params: ICellRendererParams) => {
    if (!params.value) return null;
    return (
      <Tooltip
        title={params.value}
        // arrow
        // placement="top"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  boundary: "window",
                },
              },
            ],
          },
          tooltip: {
            sx: {
              backgroundColor: "#fff",
              color: "#000",
              border: "1px solid #ddd",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
              fontSize: "14px",
            },
          },
          arrow: {
            sx: {
              color: "#fff",
            },
          },
        }}
      >
        <span
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
          }}
        >
          {params.value}
        </span>
      </Tooltip>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Success":
        return {
          color: "#00a61b",
          dotColor: "#00a61b",
          background: "rgba(0, 166, 27, .1)",
        };
      case "Failed":
        return {
          color: "#f53b3b",
          dotColor: "#f53b3b",
          background: "rgba(245, 59, 59, .1)",
        };
      case "Cancelled":
        return {
          color: "#696595",
          dotColor: "#696595",
          background: "rgba(105, 101, 149, .1)",
        };
      case "Partially Success":
        return {
          color: "#F7941F",
          dotColor: "#F7941F",
          background: "rgba(247, 148, 31, 0.1)",
        };
      default:
        return { color: "default" as "default", dotColor: "#9E9E9E" };
    }
  };

  const isSuccessDeviceStatus = (row: any) => {
    const label = String(row?.status || "").toLowerCase().trim();
    const code = Number(row?.statusTemp);
    return (
      label === "success" ||
      label === "partially success" ||
      code === 3 ||
      code === 6
    );
  };

  const getSelectedSuccessDeviceIds = (rows: any[] = selectedRows) =>
    [
      ...new Set(
        rows
          .filter((row) => isSuccessDeviceStatus(row) && row?.device_id)
          .map((row) => String(row.device_id))
      ),
    ];

  const navigateToScanDetails = (deviceIds: string[] = []) => {
    const summaryId = onprem_posture_id || scan_uuid;
    if (!scan_uuid || !summaryId) return;

    const isCloud = location.pathname.includes("/cloud/");
    const detailsPath = isCloud
      ? `/cloud/scan/${scan_uuid}`
      : `/onprem/scan/${scan_uuid}`;

    const params = new URLSearchParams({
      scan_summary_id: String(summaryId),
    });
    if (deviceIds.length > 0) {
      params.set("device_ids", deviceIds.join(","));
    }

    dispatch(
      setEdit({
        edit: true,
        scan_uuid,
        status: statusTemp != null ? String(statusTemp) : "",
        scanTime: scanTime || "",
        onprem_posture_id: summaryId,
      })
    );

    navigate(`${detailsPath}?${params.toString()}`);
  };

  const handleDeviceRowClick = (row: any) => {
    if (!row?.device_id || !isSuccessDeviceStatus(row)) return;
    navigateToScanDetails([String(row.device_id)]);
  };

  const handleScanDetailsClick = () => {
    navigateToScanDetails(getSelectedSuccessDeviceIds());
  };

  // Styled Accordions (reused from your CustomizedAccordions)

  const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
  ))(({ theme }) => ({
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:not(:last-child)": {
      marginBottom: theme.spacing(1),
    },
    "&::before": {
      display: "none",
    },
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
      {...props}
    />
  ))(({ theme }) => ({
    backgroundColor: "rgba(0, 0, 0, 0.03)",
    flexDirection: "row-reverse",
    minHeight: 40,
    padding: theme.spacing(1),
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
    [`& .${accordionSummaryClasses.content}`]: {
      marginLeft: theme.spacing(1),
      margin: 0,
    },
  }));

  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingTop: 0,
    borderTop: "none",
  }));
  const VendorCategoryDropdown = () => (
    <Box
      sx={{ width: 220, bgcolor: isDarkMode ? "#1E1E1E" : "#fff", borderRadius: 2, p: 1, boxShadow: 2 }}
    >
      {loadingVendors ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        vendors.map((vendor) => (
          <>
            <Accordion
              key={vendor.id}
              expanded={expandedVendor === vendor.id}
              onChange={(_, expanded) =>
                setExpandedVendor(expanded ? vendor.id : false)
              }
              sx={{
                boxShadow: "none !important",
                border: "none",
                background: "none !important",
                backgroundColor: "transparent !important",
                "&:before": { display: "none" },
                "&.MuiAccordion-root": { border: "none" },
                p: 1,
              }}
              elevation={0}
            >
              <AccordionSummary
                expandIcon={
                  <ArrowRightIcon sx={{ fontSize: "1.5rem", color: isDarkMode ? "#fff" : "#000" }} />
                }
                sx={{
                  border: "none",
                  background: "none !important",
                  backgroundColor: "transparent !important",
                  "&:before": { display: "none" },
                  minHeight: 0,
                  "&.MuiAccordionSummary-root": { border: "none" },
                  boxShadow: "none !important",
                }}
              >
                <Typography fontWeight={500} sx={{ color: isDarkMode ? "#E5E7EB" : "inherit" }}>{vendor.name}</Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  border: "none",
                  background: "none !important",
                  backgroundColor: "transparent !important",
                  paddingTop: 0,
                  "&.MuiAccordionDetails-root": { border: "none" },
                  boxShadow: "none !important",
                }}
              >
                <RadioGroup
                  value={selectedCategory}
                  onChange={(_, value) => setSelectedCategory(value)}
                >
                  {vendor.categories.map((cat: any) => (
                    <FormControlLabel
                      key={cat.id}
                      value={cat.id}
                      control={<Radio />}
                      label={cat.name}
                      sx={{ mb: 0.5, color: isDarkMode ? "#E5E7EB" : "inherit" }}
                    />
                  ))}
                </RadioGroup>
              </AccordionDetails>
            </Accordion>
            <Divider sx={{ borderColor: isDarkMode ? "#374151" : undefined }} />
          </>
        ))
      )}
      <Divider sx={{ my: 2, borderColor: isDarkMode ? "#374151" : "#F0F0F0" }} />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          // fullWidth
          sx={{
            bgcolor: "#3A2ADA",
            color: "#fff",
            fontWeight: 600,
          }}
          onClick={() => {
            setVendorAnchorEl(null);
            handleApply();
          }}
          disabled={!selectedCategory}
        >
          Apply & Rescan
        </Button>
      </div>
    </Box>
  );

  const selectedSuccessCount = getSelectedSuccessDeviceIds().length;

  const headerActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Button
        variant="contained"
        size="small"
        onClick={handleScanDetailsClick}
        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
      >
        Scan Details
        {selectedSuccessCount > 0 ? ` (${selectedSuccessCount})` : ""}
      </Button>
      {selectedRows.length > 0 && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography
              sx={{ color: "#888994", fontSize: "16px", fontWeight: "700" }}
            >
              Asset:
            </Typography>
            <div
              onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                handleVendorButtonClick(
                  event as unknown as React.MouseEvent<HTMLElement>
                )
              }
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: "700" }}>
                Select
              </Typography>
              <ArrowDropDownIcon sx={{ minWidth: 0, p: 0, lineHeight: 1 }} />
            </div>
          </div>
          {/* <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography
            sx={{ color: "#888994", fontSize: "16px", fontWeight: "700" }}
          >
            Region:
          </Typography>
          <div
            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
              handleRegionClick(
                event as unknown as React.MouseEvent<HTMLElement>
              )
            }
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <Typography sx={{ fontSize: "16px", fontWeight: "700" }}>
              Select
            </Typography>
            <ArrowDropDownIcon sx={{ minWidth: 0, p: 0, lineHeight: 1 }} />
          </div>
        </div> */}
        </>
      )}
      {regionDropdown}
      <Popover
        open={Boolean(regionAnchorEl)}
        anchorEl={regionAnchorEl}
        onClose={handlePoPoverRegionClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{ sx: { mt: 1, boxShadow: 3, borderRadius: 2 } }}
      >
        <Card sx={{ minWidth: 350, p: 2, boxShadow: 3 }}>
          {/* <input
          type="text"
          placeholder="Search.."
          value={regionSearch}
          onChange={e => setRegionSearch(e.target.value)}
          style={{ width: "100%", marginBottom: 8, padding: 6, borderRadius: 4, border: '1px solid #eee' }}
        /> */}
          <TextField
            variant="outlined"
            placeholder="Search..."
            size="small"
            fullWidth
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0 }}>
                  <SearchIcon sx={{ color: "#b0b4c4", fontSize: 22, mr: 0 }} />
                </InputAdornment>
              ),
              sx: {
                '& input': {
                  paddingLeft: 0, // Remove extra left padding
                },
              },
            }}
            onChange={(e) => {
              const searchValue = e.target.value.toLowerCase();
              if (searchValue) {
                const filterRegions = (regions: any[]): any[] => {
                  return regions
                    .map((region) => {
                      if (region.label.toLowerCase().includes(searchValue)) {
                        setExpanded((prev) => ({ ...prev, [region.id]: true }));
                        return region;
                      }
                      if (region.data?.length) {
                        const filteredChildren = filterRegions(region.data);
                        if (filteredChildren.length) {
                          setExpanded((prev) => ({ ...prev, [region.id]: true }));
                          return { ...region, data: filteredChildren };
                        }
                      }
                      return null;
                    })
                    .filter(Boolean);
                };
                setCurrentRegions(filterRegions(originalRegions));
              } else {
                setCurrentRegions(originalRegions);
                setExpanded({});
              }
            }}
          />
          <div style={{ maxHeight: 250, overflowY: 'auto' }}>
            {/* {regionOptions.filter(region => region.name.toLowerCase().includes(regionSearch.toLowerCase()) || region.countries.some(c => c.name.toLowerCase().includes(regionSearch.toLowerCase()))).map(region => (
            <div key={region.name} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, cursor: 'pointer', marginBottom: 4 }}>{region.name}</div>
              {region.countries.map(country => (
                <div key={country.name} style={{ marginLeft: 16, marginBottom: 4 }}>
                  <div style={{ fontWeight: 500 }}>{country.name}</div>
                  {country.states.map(state => (
                    <div key={state} style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="radio" name="regionState" />
                      <span>{state}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))} */}
            <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
              <RegionTree
                nodes={currentRegions}
                selectedId={selectedRegionId}
              />
              <div style={{ borderTop: '1px solid #e0e0e0', margin: '16px 0 0 0', width: '100%' }} />
            </Box>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Button
              variant="contained"
              sx={{
                minWidth: 100,
                borderRadius: '8px',
                background: '#4B2FFB',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: 'none',
                '&:hover': { background: '#3a23c8' }
              }}
              onClick={handleRegionClose}
            >
              Apply
            </Button>
          </div>
          {/* <Button variant="contained" sx={{ mt: 2, width: '100%' }} onClick={handleRegionClose}>Apply</Button> */}
        </Card>
      </Popover>
      <Popover
        open={vendorDropdownOpen}
        anchorEl={vendorAnchorEl}
        onClose={handleVendorDropdownClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{ sx: { mt: 1, boxShadow: 3, borderRadius: 2 } }}
      >
        <VendorCategoryDropdown />
      </Popover>
      {/* <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Region</InputLabel>
        <Select
          value={region}
          label="Region"
          onChange={(e) => setRegion(e.target.value)}
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="region1">Region 1</MenuItem>
          <MenuItem value="region2">Region 2</MenuItem>
        </Select>
      </FormControl> */}
      {/* <Button variant="outlined" color="error" sx={{ minWidth: 120, ml: 1 }}>
        Cancel Scan
      </Button> */}
    </Box>
  );

  const fetchScheduleData = async () => {
    const payloadUuid = onprem_posture_id || scan_uuid;
    const payload = {
      scan_uuid: payloadUuid,
    };
    try {
      setIsLoading(true);
      const response = await axiosInstance.post(
        END_POINTS.MY_SCAN_DETAILS,
        payload
      );
      console.log("response", response);
      if (response.data?.data?.scan_details) {
        const mappedData = response.data.data.scan_details.map(
          (schedule: any, index: any) => ({
            id: index + 1,
            file_id: schedule.file_id,
            fileName: schedule.file_name,
            assetName: schedule.asset_name,
            vendor: schedule.vendor,
            device_id: schedule.device_id,
            type: formatScanType(schedule.category),
            region: schedule.region,
            status: getStatus(Number(schedule.status)) || schedule.status,
            statusTemp: schedule.status,
            source: schedule.source,
            comments: schedule.reason,
          })
        );
        setScheduleRowData(mappedData);

        // 🚀 Populate global filter variables with unique values
        const uniqueStatuses = [...new Set(mappedData.map((item: any) => item.status).filter(Boolean))] as string[];
        const uniqueVendors = [...new Set(mappedData.map((item: any) => item.vendor).filter(Boolean))] as string[];
        const uniqueTypes = [...new Set(mappedData.map((item: any) => item.type).filter(Boolean))] as string[];
        const uniqueRegions = [...new Set(mappedData.map((item: any) => item.region).filter(Boolean))] as string[];
        const uniqueAssetNames = [...new Set(mappedData.map((item: any) => item.assetName).filter(Boolean))] as string[];

        // Update global variables
        globalScanStatuses = uniqueStatuses;
        globalScanVendors = uniqueVendors;
        globalScanTypes = uniqueTypes;
        globalScanRegions = uniqueRegions;
        globalScanAssetNames = uniqueAssetNames;

        setIsLoading(false);
      }
    } catch (error) {
      console.error("Schedule Data Fetch Failed", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // if (scan_uuid) {
    //   dispatch(setEdit({ edit: true, scan_uuid }));
    // } else {
    //   console.error("scan_uuid is undefined");
    // }
    setRegionData();
    fetchScheduleData();
  }, [scanId, scan_uuid]);

  useEffect(() => {
    if (!myScanStatusList || myScanStatusList.length === 0) return;

    console.log('[ScanInfo] 📥 myScanStatusList triggered. Raw data:', myScanStatusList);

    // Accept items that have either file_id (from scan_status_event) OR device_id (from custom_message scan_status)
    const validScanStatusList = myScanStatusList.filter(
      (item) => item.file_id || item.device_id
    );

    if (validScanStatusList.length === 0) {
      console.warn('[ScanInfo] ⚠️ No valid items in myScanStatusList — all items are missing both file_id and device_id. Raw:', myScanStatusList);
      return;
    }

    console.log('[ScanInfo] ✅ Valid items to process:', validScanStatusList.length, validScanStatusList);

    setScheduleRowData((prevData: any) => {
      const updatedData = [...prevData];
      console.log('[ScanInfo] Current table rows count:', updatedData.length);

      validScanStatusList.forEach((incomingRow: any) => {
        console.log('[ScanInfo] 🔄 Processing incoming row:', {
          file_id: incomingRow.file_id,
          device_id: incomingRow.device_id,
          scan_id: incomingRow.scan_id,
          status: incomingRow.status,
          reason: incomingRow.reason,
        });

        // Only update fields that are present in the socket data (status/reason always present)
        const mappedRow: any = {
          status: getStatus(Number(incomingRow.status)) || incomingRow.status,
          comments: incomingRow.reason,
          statusTemp: incomingRow.status,
        };

        // Map additional fields only if they exist in socket data
        if (incomingRow.file_name !== undefined) mappedRow.fileName = incomingRow.file_name;
        if (incomingRow.asset_name !== undefined) mappedRow.assetName = incomingRow.asset_name;
        if (incomingRow.vendor !== undefined) mappedRow.vendor = incomingRow.vendor;
        if (incomingRow.category !== undefined) mappedRow.type = formatScanType(incomingRow.category);
        if (incomingRow.region !== undefined) mappedRow.region = incomingRow.region;

        console.log('[ScanInfo] Mapped status fields:', mappedRow);

        // Match by file_id first (scan_status_event), then fall back to device_id (custom_message scan_status)
        let existingIndex = -1;
        let matchedBy = '';

        if (incomingRow.file_id) {
          existingIndex = updatedData.findIndex(
            (row) => row.file_id === incomingRow.file_id
          );
          if (existingIndex !== -1) {
            matchedBy = `file_id=${incomingRow.file_id}`;
            console.log(`[ScanInfo] ✅ Row matched by file_id at index ${existingIndex}:`, updatedData[existingIndex]);
          } else {
            console.log(`[ScanInfo] ⚠️ file_id=${incomingRow.file_id} — no matching row found. Will try device_id...`);
          }
        }

        if (existingIndex === -1 && incomingRow.device_id) {
          existingIndex = updatedData.findIndex(
            (row) => row.device_id === incomingRow.device_id
          );
          if (existingIndex !== -1) {
            matchedBy = `device_id=${incomingRow.device_id}`;
            console.log(`[ScanInfo] ✅ Row matched by device_id at index ${existingIndex}:`, updatedData[existingIndex]);
          } else {
            console.warn(`[ScanInfo] ❌ device_id=${incomingRow.device_id} — no matching row found either.`);
          }
        }

        if (existingIndex !== -1) {
          console.log(`[ScanInfo] 🟢 Updating row at index ${existingIndex} (matched by ${matchedBy}). Before:`, updatedData[existingIndex], '| Patch:', mappedRow);
          updatedData[existingIndex] = {
            ...updatedData[existingIndex],
            ...mappedRow,
          };
          console.log(`[ScanInfo] Row after update:`, updatedData[existingIndex]);
        } else if (incomingRow.file_id) {
          // Only add new row if we have a file_id to anchor it
          console.log('[ScanInfo] 🆕 No existing row found — inserting new row with file_id:', incomingRow.file_id);
          updatedData.unshift({
            id: updatedData.length + 1,
            file_id: incomingRow.file_id,
            device_id: incomingRow.device_id,
            ...mappedRow,
          });
        } else {
          console.warn('[ScanInfo] ⚠️ No match and no file_id — skipping row insert. device_id:', incomingRow.device_id);
        }
      });

      // 🚀 Update global filter variables with updated data
      const uniqueStatuses = [...new Set(updatedData.map((item: any) => item.status).filter(Boolean))] as string[];
      const uniqueVendors = [...new Set(updatedData.map((item: any) => item.vendor).filter(Boolean))] as string[];
      const uniqueTypes = [...new Set(updatedData.map((item: any) => item.type).filter(Boolean))] as string[];
      const uniqueRegions = [...new Set(updatedData.map((item: any) => item.region).filter(Boolean))] as string[];
      const uniqueAssetNames = [...new Set(updatedData.map((item: any) => item.assetName).filter(Boolean))] as string[];

      // Update global variables
      globalScanStatuses = uniqueStatuses;
      globalScanVendors = uniqueVendors;
      globalScanTypes = uniqueTypes;
      globalScanRegions = uniqueRegions;
      globalScanAssetNames = uniqueAssetNames;

      console.log('[ScanInfo] ✅ Final updated table rows count:', updatedData.length);
      return updatedData;
    });
    setTimeout(() => {
      gridApiRef.current?.api?.refreshCells({ force: true });
    }, 100);
  }, [myScanStatusList, scan_uuid]);

  useEffect(() => {
    if (myscanSocketData && myscanSocketData.length > 0) {
      const hasScanUploaded = myscanSocketData.some(
        (item: any) => item.action === "scan_uploaded" && (item.scan_uuid === scan_uuid || item.scan_uuid === onprem_posture_id)
      );
      if (hasScanUploaded) {
        console.log('[ScanInfo] 📥 myscanSocketData triggered refresh due to scan_uploaded');
        fetchScheduleData();
      }
    }
  }, [myscanSocketData, scan_uuid, onprem_posture_id]);

  const canSelectDeviceRow = (row: any) => {
    if (!row) return false;
    if (isSuccessDeviceStatus(row)) return true;

    const status = row.statusTemp;
    const source = row.source;
    const scanTimeStr = scanTime;
    if (
      (source === "ssh" || source === "onprem_posture") &&
      (Number(status) === 9 || Number(status) === 4) &&
      scanTimeStr
    ) {
      try {
        const scanTimeMs = new Date(scanTimeStr).getTime();
        const now = Date.now();
        const diffHours = (now - scanTimeMs) / (1000 * 60 * 60);
        return diffHours < 1;
      } catch {
        return false;
      }
    }
    return false;
  };

  const scanColumnDefs: ColDef[] = [
    {
      field: "selection",
      headerName: "",
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      flex: 0,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: true,
      lockPinned: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
    },
    {
      field: "selectionAndFileName",
      headerName: "File Name",
      minWidth: 250,
      width: 250,
      sortable: false,
      filter: false,
      resizable: true,
      suppressSizeToFit: true,

      cellRenderer: (params: ICellRendererParams) => {
        const fileLabel = params.data.fileName ?? params.data.file_id;

        return (
          <WhiteTooltip title={fileLabel || ""} placement="top">
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
                width: "100%",
              }}
            >
              {fileLabel}
            </span>
          </WhiteTooltip>
        );
      },
    },
    // {
    //   field: "selectionAndFileName",
    //   headerName: "",
    //   minWidth: 250,
    //   width: 250,
    //   sortable: false,
    //   filter: false,
    //   resizable: false,
    //   suppressMovable: true,
    //   lockPosition: true,
    //   lockPinned: true,
    //   suppressSizeToFit: true,

    //   // checkboxSelection: (params) => {
    //   //   const status = params.data?.statusTemp;
    //   //   const scanTimeStr = scanTime;

    //   //   if ((status === 9 ) && scanTimeStr) {
    //   //     try {
    //   //       const scanTime = new Date(scanTimeStr).getTime();
    //   //       const now = Date.now();
    //   //       const diffHours = (now - scanTime) / (1000 * 60 * 60);
    //   //       return diffHours < 1;
    //   //     } catch (e) {
    //   //       return false;
    //   //     }
    //   //   }

    //   //   return false;
    //   // },

    //   cellRenderer: (params: ICellRendererParams) => {
    //     const status = params.data?.statusTemp;
    //     const scanTimeStr = scanTime;
    //     let showCheckbox = false;

    //     if (status === 9 && scanTimeStr) {
    //       try {
    //         const scanTime = new Date(scanTimeStr).getTime();
    //         const now = Date.now();
    //         const diffMs = now - scanTime;
    //         const diffHours = diffMs / (1000 * 60 * 60);
    //         if (diffHours < 1) {
    //           showCheckbox = true;
    //         }
    //       } catch (e) {
    //         showCheckbox = false;
    //       }
    //     }

    //     return (
    //       <span style={{ display: 'flex', alignItems: 'center', maxWidth: 220 }}>
    //         {showCheckbox && (
    //           <>
    //             <input
    //               type="checkbox"
    //               checked={params.node.isSelected()}
    //               onChange={() => {
    //                 params.node.setSelected(!params.node.isSelected());
    //               }}
    //               style={{ marginRight: 8 }}
    //             />
    //             {"sdfsdf"}
    //           </>
    //         )}
    //         <span
    //           style={{
    //             whiteSpace: 'nowrap',
    //             overflow: 'hidden',
    //             textOverflow: 'ellipsis',
    //             display: 'inline-block',
    //             maxWidth: 170
    //           }}
    //           title={params.data.fileName ?? params.data.file_id}
    //         >
    //           {params.data.fileName ?? params.data.file_id}
    //         </span>
    //       </span>
    //     );
    //   },
    // },

    {
      field: "assetName",
      headerName: "Asset Name",
      filter: ScanAssetNameFilter,
      filterParams: {
        isDarkMode: theme.palette.mode === 'dark',
        scheduleStatuses: globalScanAssetNames,
      },
      sortable: true,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => {
        return <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {params.value}
        </span>
      },
    },
    {
      field: "vendor",
      headerName: "Vendor",
      filter: ScanVendorFilter,
      filterParams: {
        isDarkMode: theme.palette.mode === 'dark',
        scheduleStatuses: globalScanVendors.filter(vendor => vendor !== "NA"),
      },
      sortable: true,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        return <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {params.value}
        </span>
      },
    },
    {
      field: "type",
      headerName: "Type",
      filter: ScanTypeFilter,
      filterParams: {
        isDarkMode: theme.palette.mode === 'dark',
        scheduleStatuses: globalScanTypes,
      },
      sortable: false,
      minWidth: 160,
      width: 160,
      cellRenderer: (params: ICellRendererParams) => {
        return <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {formatScanType(params.value)}
        </span>
      },
    },
    {
      field: "region",
      headerName: "Region",
      filter: ScanRegionFilter,
      filterParams: {
        isDarkMode: theme.palette.mode === 'dark',
        scheduleStatuses: globalScanRegions,
      },
      sortable: false,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => {
        return <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {params.value}
        </span>
      },
    },
    {
      field: "status",
      headerName: "Status",
      filter: ScanStatusFilter,
      filterParams: {
        isDarkMode: theme.palette.mode === 'dark',
        scheduleStatuses: globalScanStatuses.filter(status => status !== "NA"),
      },
      sortable: false,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null;
        const { color, background } = getStatusColor(params.value);

        return (
          <Chip
            label={
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {params.value}
              </span>
            }
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: color,
              maxWidth: "fit-content",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              mt: 1,
              backgroundColor: background,
            }}
          />
        );
      },
    },
    {
      field: "comments",
      headerName: "Comments",
      filter: true,
      sortable: false,
      minWidth: 200,
      cellRenderer: CustomTooltipCell,
    },
  ];

  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const response = await axiosInstance.get("securityscan/vendors-models");
        setVendors(response.data?.data?.vendors || []);
      } catch (error) {
        setVendors([]);
      }
      setLoadingVendors(false);
    };
    fetchVendors();
  }, []);

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: false,
  };

  const gridOptions: GridOptions = {
    defaultColDef,
    rowHeight: 48,
    headerHeight: 48,
    suppressCellFocus: true,
    getRowStyle: (params) => ({
      cursor: isSuccessDeviceStatus(params?.data) ? "pointer" : "default",
    }),
    isRowSelectable: (node) => canSelectDeviceRow(node?.data),
    suppressRowClickSelection: true,
    rowMultiSelectWithClick: false,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    rowSelection: "multiple" as const,
    onSelectionChanged: (event: any) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node: any) => node.data);
      setSelectedRows(selectedData);
      console.log("Selected rows:", selectedData);
    },
  };

  return (
    <Card
      variant="outlined"
      sx={{ height: "calc(100vh - 120px)", borderRadius: 3 }}
    >
      <AgGridTable
        columnDefs={scanColumnDefs}
        data={scheduleRowData}
        height={"calc(89vh - 120px)"}
        showSearch
        headerActions={headerActions}
        isLoading={isLoading}
        onSelectionChange={handleSelectionChange}
        isRowSelectable={true}
        checkboxSelection
        headerCheckboxSelection
        rowSelection="multiple"
        gridOptions={gridOptions}
        gridRef={gridApiRef}
        activeFilters={activeFilters}
        onRowClick={handleDeviceRowClick}
      />
      <Dialog
        open={isCancelSchedulePopupOpen}
        onClose={() => handleCancelScheduleClose()}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Select Credential</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: isDarkMode ? "#fff" : "#666666", fontSize: "16px" }}>
            Select a credential option to rescan the asset.
          </Typography>
          <RadioGroup
            value={removeAllSchedules ? "remove_all" : "remove_my"}
            onChange={(e) =>
              setRemoveAllSchedules(e.target.value === "remove_all")
            }
          >
            <FormControlLabel
              value="remove_my"
              control={<Radio />}
              label="Use existing credential"
              sx={{
                "& .MuiTypography-root": {
                  color: !removeAllSchedules ? (isDarkMode ? "#8C9EFF" : "blue") : (isDarkMode ? "#fff" : "black"),
                },
              }}
            />
            <FormControlLabel
              value="remove_all"
              control={<Radio />}
              label="Map other credential"
              sx={{
                "& .MuiTypography-root": {
                  color: removeAllSchedules ? (isDarkMode ? "#8C9EFF" : "blue") : (isDarkMode ? "#fff" : "black"),
                },
              }}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            paddingBottom: "24px",
            // marginTop: "4px",
          }}
        >
          {/* <Button
                        onClick={handleCancelScheduleClose}
                        variant="outlined"
                        color="secondary"
                        sx={{ width: 120 }}
                    >
                        Cancel
                    </Button> */}
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", width: "100%" }}>
            <Button
              onClick={handleRemoveSchedule}
              variant="contained"
              color="primary"
              sx={{ width: 150, mr: 2 }}
            >
              Continue
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <MapCredentialPopup
        open={isMapCredentialPopupOpen}
        onClose={handleMapPopuplose}
        assetsCredData={assetsCredData}
        credData={credTypes}
        deviceId={null}
        mapCallback={mapCallback}
      />
    </Card>
  );
};

export default ScanInformation;
